import React from 'react';
import PropTypes from 'prop-types';
import { ButtonBase, Typography } from '@material-ui/core';
import { makeStyles, lighten } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import TooltipIconButton from '../../widgets/TooltipIconButton';
import { seedMyInputImageMultipleIcons } from '../../icon/seed-icons';

const useStyles = makeStyles((theme) => ({
  input: {
    display: 'none',
  },
  image: {
    marginTop: theme.spacing(2),
    position: 'relative',
    height: 100,
    border: (props) => (props.error ? `2px solid ${theme.palette.error.main}` : 'none'),
    [theme.breakpoints.down('xs')]: {
      width: '100% !important', // Overrides inline-style
      height: 100,
    },
    '&:hover, &$focusVisible': {
      zIndex: 1,
      '& $imageBackdrop': {
        opacity: 0.4,
      },
      '& $imageTitle': {
        opacity: 1,
      },
    },
  },
  focusVisible: {},
  imageSrc: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center 40%',
  },
  imageBackdrop: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: theme.palette.common.black,
    opacity: 0.15,
    transition: theme.transitions.create('opacity'),
  },
  imageTitle: {
    color: 'white',
    display: 'inherit',
    fontSize: 12,
    maxWidth: 36,
    opacity: 0.0,
    position: 'relative',
    padding: `${theme.spacing(2)}px ${theme.spacing(4)}px ${theme.spacing(1) + 6}px`,
  },
  deleteButtonTooltip: {
    position: 'relative',
    zIndex: 1,
  },
  deleteButton: {
    position: 'absolute',
    right: 0,
    marginTop: theme.spacing(2),
    color: 'white',
    '&:hover': {
      backgroundColor: lighten(theme.palette.error.main, 0.5),
    },
  },
}));

const MyInputImageMultiple = React.forwardRef((props, ref) => {
  const {
    name,
    defaultValues,
    onChange,

    autoFocus,
    disabled,
    error,

    label,
    icons,
  } = props;

  const inputRef = React.useRef(null);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [width] = React.useState(100);
  const [height] = React.useState(100);
  const classes = useStyles({ error });

  const [values, setValues] = React.useReducer((pictures, { type, value, index: updateIndex }) => {
    switch (type) {
      case 'add':
        if (onChange) {
          onChange({
            skipPersist: true,
            target: {
              name,
              value: [...pictures, value],
            },
          });
        }
        return [...pictures, value];
      case 'update':
        if (onChange) {
          onChange({
            skipPersist: true,
            target: {
              name,
              value: pictures.map((oldValue, index) => (index === updateIndex ? value : oldValue)),
            },
          });
        }
        return pictures.map((oldValue, index) => (index === updateIndex ? value : oldValue));
      case 'remove':
        if (onChange) {
          onChange({
            skipPersist: true,
            target: {
              name,
              value: pictures.filter((_, index) => index !== value),
            },
          });
        }
        return pictures.filter((_, index) => index !== value);
      case 'clear':
        if (onChange) {
          onChange({ skipPersist: true, target: { name, value: [] } });
        }
        return [];
      default:
        return values;
    }
  }, defaultValues);
  const [previews, setPreviews] = React.useReducer((curPreviews, {
    type,
    value,
    index: updateIndex,
  }) => {
    switch (type) {
      case 'add':
        return [...curPreviews, value];
      case 'update':
        return curPreviews.map((oldValue, index) => (index === updateIndex ? value : oldValue));
      case 'remove':
        return curPreviews.filter((_, index) => index !== value);
      case 'clear':
        return [];
      default:
        return curPreviews;
    }
  }, defaultValues);

  React.useImperativeHandle(ref, () => ({
    getValues() {
      return values;
    },
  }));

  const handleChangeInput = (file) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        if (values.length === currentIndex) {
          setValues({ type: 'add', value: file });
        } else {
          setValues({ type: 'update', value: file, index: currentIndex });
        }
        if (previews.length === currentIndex) {
          setPreviews({ type: 'add', value: e.target.result });
        } else {
          setPreviews({ type: 'update', value: e.target.result, index: currentIndex });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClickDelete = (index) => {
    setPreviews({ type: 'remove', value: index });
    setValues({ type: 'remove', value: index });
  };

  return (
    <>
      <input
        accept="image/*"
        className={classes.input}
        type="file"
        ref={inputRef}
        onChange={(e) => { handleChangeInput(e.target.files[0]); }}
      />
      <label htmlFor={name}>
        {
          previews.map((preview, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={`my-input-file-multiple-preview-${index}`} style={{ display: 'inline-flex' }}>
              <ButtonBase
                focusRipple
                className={classes.image}
                focusVisibleClassName={classes.focusVisible}
                onClick={() => {
                  setCurrentIndex(index);
                  inputRef.current.click();
                }}
                disabled={disabled}
                style={{ height, width }}
              >
                <span
                  className={classes.imageSrc}
                  style={{
                    backgroundImage: `url(${preview})`,
                  }}
                />
                <span className={classes.imageBackdrop} />
                <span className={classes.imageButton}>
                  <Typography
                    component="span"
                    color="inherit"
                    className={classes.imageTitle}
                  >
                    <FormattedMessage id="myInputImageMultiple.change.label" values={{ label }} />
                  </Typography>
                </span>
              </ButtonBase>
              <TooltipIconButton
                icon={<icons.Delete style={{ fontSize: 20 }} />}
                title={<FormattedMessage id="myInputImageMultiple.delete" />}

                disabled={disabled}
                onClick={() => handleClickDelete(index)}
                className={classes.deleteButton}
                size="small"

                tooltipProps={{
                  className: classes.deleteButtonTooltip,
                }}
              />
            </div>
          ))
        }
        <div style={{ display: 'inline-flex' }}>
          <ButtonBase
            autoFocus={autoFocus}
            focusRipple
            className={classes.image}
            focusVisibleClassName={classes.focusVisible}
            onClick={() => {
              setCurrentIndex(previews.length);
              inputRef.current.click();
            }}
            disabled={disabled}
            style={{ height, width }}
          >
            <span
              className={classes.imageSrc}
            />
            <span className={classes.imageBackdrop} />
            <span className={classes.imageButton}>
              <Typography
                component="span"
                color="inherit"
                className={classes.imageTitle}
              >
                <FormattedMessage id="myInputImageMultiple.add.label" values={{ label }} />
              </Typography>
            </span>
          </ButtonBase>
        </div>
      </label>
    </>
  );
});

MyInputImageMultiple.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  defaultValues: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(File),
    ]),
  ),

  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,

  label: PropTypes.node.isRequired,
  icons: PropTypes.shape({
    Delete: PropTypes.object.isRequired,
  }),
};

MyInputImageMultiple.defaultProps = {
  onChange: undefined,
  defaultValues: [],

  autoFocus: false,
  disabled: false,
  error: false,
  icons: seedMyInputImageMultipleIcons,
};

export default MyInputImageMultiple;
